import { isDefined, isNonEmpty } from '@sgme/fp';
import { QuoteResultDto } from 'apiConnector/quote/dto/quoteResultDto';
import { EssentialPersistedTabContent } from 'apiConnector/tabs/tabs.model';
import { IdType } from 'epic/common-form/ReloadFormRequest';
import type { FormError } from 'models/formError';
import { EssentialFormResult } from 'models/forms/essential';
import type { ProductLayoutTree } from 'models/forms/FormResult';
import { AllowedRequests } from 'models/forms/FormResult';
import { ReadonlyValueDefinition } from 'models/metadata/readonlyValue.definition';
import { batch } from 'react-redux';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { buildProductDescriptionKey, buildRemunerationMode, getInstrumentName } from 'state/common-form/form.helper';
import { selectEssentialFormInfos, selectEssentialsFormQuoteId } from 'state/essential/infos/selectors';
import { formEnrichedDataReceived } from 'state/formData/formData.action';
import type { DataById, FormFieldId } from 'state/formData/formData.model';
import { checkFormEnrichmentRequestNeededThunk } from 'state/formData/formData.thunks';
import { commonFormActions } from 'state/forms/actions/form';
import { commonQuoteAction } from 'state/forms/actions/quote';
import { triggerDocumentationFetchThunk } from 'state/forms/documentation.thunks';
import { dispatchCommonFormActionsForWorkspace } from 'state/forms/thunks';
import type { EssentialProductView } from 'state/product-description/types';
import { QuoteDetailsData } from 'state/quotes/details/details.model';
import { QuoteType } from 'state/quotes/infos/infos.model';
import { pendingQuoteDetailsSaved } from 'state/quotes/pendingDetails/pendingDetails.action';
import { priceImprovementInitialized } from 'state/quotes/priceEnhancements/priceImprovementRequest/priceImprovementRequest.actions';
import { notUsedQuoteCleanedThunk } from 'state/quotes/quotes.thunks';
import { isQuoteTypeWithRegulation } from 'state/quotes/utils';
import { getFormEnrichedData } from 'state/selectors';
import type { Thunk } from 'state/state.common';
import { updateSidePanelMetadata } from 'state/uiPanelState/sidePanelUi/sidePanelUi.action';
import { synchronizeWorkspaceProductVariationWithFormValuesThunk } from 'state/workspaces/essential/thunks';
import { TabsForWorkspace, WorkspaceType } from 'state/workspaces/model';
import {
  workspaceQuoteReloaded,
  workspaceQuoteReloadRequested,
  workspaceTabCloned,
  workspaceTabFrozen,
  workspaceTabUnfrozen,
} from 'state/workspaces/workspace.actions';
import { ApiErrorMessage } from 'utils/http/apiErrorHelper';
import { verifyQuoteImportIdentifiers } from 'utils/import/importQuote';
import { getToastUtils } from 'utils/toast';

import type { FormBlockMetadataById } from '../../models/metadata/block.definition';
import type { QuoteAction } from '../common-form/infos/infos.model';
import type { FieldMetadataById } from '../common-form/metaData/fields/fields.model';

const dispatchCommonFormActions = dispatchCommonFormActionsForWorkspace('essential');

export function defaultFormReceivedThunk(
  tabId: string,
  productView: EssentialProductView,
  blocksMetadata: FormBlockMetadataById,
  fieldsMetadata: FieldMetadataById,
  values: DataById,
  version: number,
  negotiationId: string | undefined,
  actions: QuoteAction[],
  quoteQuery: unknown,
  mustForceAdvancedMode: boolean,
  allowedRequests: AllowedRequests,
  validationErrors?: FormError[],
  sendWholeFormData?: boolean,
  productLayout?: ProductLayoutTree,
): Thunk<void> {
  return (dispatch, _getState) => {
    dispatch(
      commonFormActions.defaultStatifiedFormReceived(
        'essential',
        tabId,
        productView,
        buildProductDescriptionKey(values),
        getInstrumentName(values),
        blocksMetadata,
        fieldsMetadata,
        version,
        negotiationId,
        actions,
        quoteQuery,
        buildRemunerationMode(values),
        mustForceAdvancedMode,
        validationErrors,
        sendWholeFormData,
        productLayout,
      ),
    );

    dispatchCommonFormActions(dispatch, tabId, values, allowedRequests);

    dispatch(triggerDocumentationFetchThunk(tabId, 'essential'));
  };
}

export function essentialTabReloadedThunk(tabId: string, tabContent: EssentialPersistedTabContent): Thunk<void> {
  return (dispatch, _getState) => {
    const {
      negotiationId,
      blocksMetadata,
      fieldsMetadata,
      values,
      version,
      actions,
      quoteQuery,
      errors,
      productView,
      mustForceAdvancedMode,
      quoteId,
      sendWholeFormData,
      allowedRequests,
      productLayout,
    } = tabContent;
    dispatch(
      essentialFormReloadedThunk(
        tabId,
        productView,
        negotiationId,
        blocksMetadata,
        fieldsMetadata,
        values,
        version,
        actions,
        quoteQuery,
        mustForceAdvancedMode,
        allowedRequests,
        errors,
        sendWholeFormData,
        productLayout,
      ),
    );
    if (quoteId !== undefined) {
      dispatch(commonQuoteAction.previousQuoteLoadRequested('essential', tabId, quoteId));
    }
  };
}

export function essentialFormReloadedThunk(
  tabId: string,
  productView: EssentialProductView,
  negotiationId: string | undefined,
  blocksMetadata: FormBlockMetadataById,
  fieldsMetadata: FieldMetadataById,
  values: DataById,
  version: number,
  actions: QuoteAction[],
  quoteQuery: unknown,
  mustForceAdvancedMode: boolean,
  allowedRequests: AllowedRequests,
  validationErrors?: FormError[],
  sendWholeFormData?: boolean,
  productLayout?: ProductLayoutTree,
): Thunk<void> {
  return (dispatch, _getState) => {
    const productDescription = buildProductDescriptionKey(values);
    const remunerationMode = buildRemunerationMode(values);
    dispatch(
      commonFormActions.statifiedFormReloaded('essential', tabId, {
        productView,
        negotiationId,
        productDescription,
        instrumentName: getInstrumentName(values),
        blocksMetadata,
        fieldsMetadata,
        version,
        actions,
        quoteQuery,
        remunerationMode,
        mustForceAdvancedMode,
        validationErrors: isNonEmpty(validationErrors) ? validationErrors : undefined,
        sendWholeFormData,
        productLayout,
      }),
    );

    dispatchCommonFormActions(dispatch, tabId, values, allowedRequests);

    dispatch(synchronizeWorkspaceProductVariationWithFormValuesThunk(tabId, values));

    dispatch(triggerDocumentationFetchThunk(tabId, 'essential'));
  };
}

export function essentialFormEnrichmentReceivedThunk(
  tabId: string,
  productView: EssentialProductView,
  blocksMetadata: FormBlockMetadataById,
  fieldsMetadata: FieldMetadataById,
  values: DataById,
  version: number,
  actions: QuoteAction[],
  quoteQuery: unknown,
  mustForceAdvancedMode: boolean,
  allowedRequests: AllowedRequests,
  validationErrors?: FormError[],
  productLayout?: ProductLayoutTree,
): Thunk<void> {
  return dispatch => {
    batch(() => {
      dispatch(
        commonFormActions.statifiedFormEnrichmentReceived(
          'essential',
          tabId,
          productView,
          buildProductDescriptionKey(values),
          getInstrumentName(values),
          blocksMetadata,
          fieldsMetadata,
          version,
          actions,
          quoteQuery,
          buildRemunerationMode(values),
          mustForceAdvancedMode,
          validationErrors,
          productLayout,
        ),
      );

      dispatchCommonFormActions(dispatch, tabId, values, allowedRequests);

      dispatch(checkFormEnrichmentRequestNeededThunk(tabId, tabId, 'essential', fieldsMetadata));
      dispatch(synchronizeWorkspaceProductVariationWithFormValuesThunk(tabId, values));

      dispatch(triggerDocumentationFetchThunk(tabId, 'essential'));
    });
  };
}

export function essentialRemoveQuoteThunk(formId: string, quoteId: string): Thunk<void> {
  return dispatch => {
    dispatch(commonQuoteAction.quoteRemoved('essential', formId, quoteId));
    dispatch(notUsedQuoteCleanedThunk(formId, quoteId));
  };
}

export function essentialRemoveQuoteByFormIdThunk(formId: string): Thunk<void> {
  return (dispatch, getState) => {
    const quoteId = selectEssentialsFormQuoteId(getState(), formId);
    if (quoteId) {
      dispatch(essentialRemoveQuoteThunk(formId, quoteId));
    } else {
      dispatch(commonQuoteAction.quoteRemoved('essential', formId));
    }
  };
}

export function essentialPriceRequestedThunk(formId: string, quoteType: QuoteType): Thunk<void> {
  return (dispatch, getState, { getNewGuid, getNow }) => {
    const state = getState();
    const frontReconciliationId = getNewGuid();
    const quoteId = selectEssentialsFormQuoteId(state, formId);

    if (quoteId) {
      dispatch(essentialRemoveQuoteThunk(formId, quoteId));
    }
    const isReprice = false;

    dispatch(
      commonQuoteAction.quoteRequested(
        'essential',
        formId,
        quoteType,
        getNow().getTime(),
        frontReconciliationId,
        isReprice,
      ),
    );
    dispatch(workspaceTabFrozen(formId, 'essential', true));
  };
}

export function essentialQuoteRequestFailedThunk(
  formId: string,
  error: ApiErrorMessage,
  frontReconciliationId: string,
): Thunk<void> {
  return dispatch => {
    dispatch(commonQuoteAction.quoteRequestFailed('essential', formId, error, frontReconciliationId));

    if (error.newVersion !== undefined) {
      dispatch(commonFormActions.updateRfqVersion('essential', formId, error.newVersion));
    }

    if (error.status === 400 && error.functionalErrorType === 'rfq-version-mismatch') {
      dispatch(commonFormActions.fetchFormEnrichmentFailed('essential', formId, error as Error));
    }
  };
}

export function essentialQuoteAckedThunk(
  tabId: string,
  quoteId: string,
  quoteType: QuoteType,
  externalQuoteId: string,
  negotiationId: string | undefined,
  estimatedQuotingDurationInMillis: number,
  quoteRequestStartingTimeInMillis: number,
  newVersion: number,
  isExecOnlyAddon: boolean,
  quoteDetails: QuoteDetailsData,
  frontReconciliationId: string,
  sidePanelDetails: readonly ReadonlyValueDefinition[] | undefined,
): Thunk<void> {
  return (dispatch, _, { getNow }) => {
    const isKidExpected = isQuoteTypeWithRegulation(quoteType);
    dispatch(
      commonQuoteAction.quoteAcked(
        'essential',
        tabId,
        quoteId,
        quoteType,
        externalQuoteId,
        negotiationId,
        estimatedQuotingDurationInMillis,
        quoteRequestStartingTimeInMillis,
        newVersion,
        isExecOnlyAddon,
        isKidExpected,
        frontReconciliationId,
        quoteDetails,
        getNow(),
        sidePanelDetails,
      ),
    );

    dispatch(
      pendingQuoteDetailsSaved(quoteId, 'essential', {
        start: new Date(quoteRequestStartingTimeInMillis),
        durationInMs: estimatedQuotingDurationInMillis,
      }),
    );
  };
}

export function essentialQuoteResultReceivedThunk(
  formId: string,
  quoteId: string,
  data: QuoteResultDto<EssentialFormResult>,
): Thunk<void> {
  return (dispatch, getState, { getNow }) => {
    const state = getState();
    const infos = selectEssentialFormInfos(state, formId);
    const formResult = data.formResult;
    const isQuoteRejected = data.status === 'Rejected' || data.status === 'Failed';
    const when = getNow();

    dispatch(commonQuoteAction.quoteReceived('essential', formId, quoteId, data, when));
    dispatch(updateSidePanelMetadata(formId, { tradeWay: data.tradeWay }));

    if (isQuoteRejected) {
      dispatch(workspaceTabUnfrozen(formId, 'essential', undefined));
    }

    dispatch(priceImprovementInitialized(quoteId));

    if (formResult !== undefined && infos !== undefined) {
      const {
        productView,
        values,
        blocksMetadata,
        fieldsMetadata,
        quoteQuery,
        actions,
        errors,
        version,
        mustForceAdvancedMode,
        productLayout,
      } = formResult;
      dispatch(
        commonFormActions.statifiedFormEnrichmentReceived(
          'essential',
          formId,
          productView,
          infos.productDescription,
          infos.instrumentName,
          blocksMetadata,
          fieldsMetadata,
          version,
          actions,
          quoteQuery,
          infos.remunerationMode,
          mustForceAdvancedMode,
          errors,
          productLayout,
        ),
      );
      dispatch(formEnrichedDataReceived(formId, formId, values, 'essential'));
      dispatch(triggerDocumentationFetchThunk(formId, 'essential'));
    }
  };
}

export function essentialQuoteCancelledThunk(formId: string): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const formInfos = selectEssentialFormInfos(state, formId);
    const quoteId = formInfos?.quoteId;
    const productSubtype = getFormEnrichedData(state, formId)?.['ProductSubtype'] as string | undefined;
    const productType = getFormEnrichedData(state, formId)?.['ProductType'] as string | undefined;

    dispatch(
      commonQuoteAction.quoteCancelled(
        'essential',
        formId,
        formInfos?.negotiationId,
        quoteId,
        productSubtype,
        productType,
      ),
    );

    if (quoteId) {
      dispatch(commonQuoteAction.quoteRemoved('essential', formId, quoteId));
      dispatch(notUsedQuoteCleanedThunk(formId, quoteId));
    }
  };
}
export function essentialFormTabRollbackedThunk(negotiationId: string, tabId: string): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();

    const quoteId = selectEssentialsFormQuoteId(state, tabId);
    if (quoteId) {
      dispatch(essentialQuoteRemovedThunk(tabId, quoteId));
    }
    dispatch(commonFormActions.formTabRollbacked('essential', tabId, negotiationId));
  };
}

function essentialQuoteRemovedThunk(tabId: string, quoteId?: string): Thunk<void> {
  return (dispatch, getState) => {
    if (!isDefined(quoteId)) {
      const state = getState();
      quoteId = selectEssentialsFormQuoteId(state, tabId);
    }

    if (isDefined(quoteId)) {
      dispatch(commonQuoteAction.quoteRemoved('essential', tabId, quoteId));
      dispatch(notUsedQuoteCleanedThunk(tabId, quoteId));
    } else {
      dispatch(commonQuoteAction.quoteRemoved('essential', tabId));
    }
  };
}

export function essentialWorkspaceTabClonedThunk<W extends WorkspaceType>(
  oldTabId: string,
  workspaceType: W,
  tabType: TabsForWorkspace[W],
): Thunk<void> {
  return (dispatch, getState, { getNewGuid }) => {
    const state = getState();
    const infos = selectEssentialFormInfos(state, oldTabId);
    const enrichedData = getFormEnrichedData(state, oldTabId);
    const newTabId = getNewGuid();

    dispatch(
      workspaceTabCloned(
        oldTabId,
        newTabId,
        infos?.negotiationId,
        workspaceType,
        tabType,
        enrichedData.ProductType as string,
        enrichedData.ProductSubtype as string,
        enrichedData.Wrapper as string,
      ),
    );
  };
}

export function essentialImportQuotesThunk(identifiers: string[]): Thunk<void> {
  return (dispatch, _state, { getNewGuid, getTodayString }) => {
    const toast = getToastUtils(dispatch);
    let identifiersValidation = verifyQuoteImportIdentifiers(identifiers);

    if (identifiersValidation.valid.length > 10) {
      toast.warn(
        {
          id: 'essential.workspace.quote.import.maxNbOfFormsImportedReached',
          values: {
            nbOfQuotes: identifiersValidation.valid.length,
          },
        },
        {
          id: 'essential.workspace.quote.import.maxNbOfFormsImportedReached.title',
          values: { nbOfQuotesRejected: identifiersValidation.valid.length - 10 },
        },
        false,
      );

      identifiersValidation = {
        valid: identifiersValidation.valid.slice(0, 10),
        invalid: identifiersValidation.invalid,
      };

      logAnalytics.trackEvent('Primary market essential', 'Import quotes - Max number of quotes to import reached');

      return;
    }

    if (identifiersValidation.valid.length > 0) {
      toast.success(
        {
          id: 'essential.workspace.quote.import.accepted',
          values: { nbOfQuotes: identifiersValidation.valid.length },
        },
        { id: 'toast.essentialWorkspace' },
      );

      logAnalytics.trackEvent('Primary market essential', 'Import quotes - successfully imported');
    }

    if (identifiersValidation.invalid.length > 0) {
      toast.warn(
        {
          id: 'essential.workspace.quote.import.discarded',
          values: { nbOfQuotes: identifiersValidation.invalid.length },
        },
        { id: 'toast.essentialWorkspace' },
        false,
      );

      logAnalytics.trackEvent(
        'Primary market essential',
        'Import quotes - discarded due to incorrect identifier format',
      );
    }

    identifiersValidation.valid.forEach(identifier => {
      const temporaryFormId = getNewGuid();
      dispatch(workspaceQuoteReloadRequested('essential', temporaryFormId, identifier, IdType.Guess, getTodayString()));
    });
  };
}

export function essentialReloadFormFromQuoteThunk(
  tabId: string,
  quoteId: string,
  productView: EssentialProductView,
  negotiationId: string | undefined,
  blocksMetadata: FormBlockMetadataById,
  fieldsMetadata: FieldMetadataById,
  values: DataById,
  version: number,
  actions: QuoteAction[],
  quoteQuery: unknown,
  mustForceAdvancedMode: boolean,
  allowedRequests: AllowedRequests,
  validationErrors?: FormError[],
  sendWholeFormData?: boolean,
  productLayout?: ProductLayoutTree,
): Thunk<void> {
  return dispatch => {
    const productSubtype = values['ProductSubtype' satisfies FormFieldId] as string;
    const productType = values['ProductType' satisfies FormFieldId] as string;
    const wrapper = values['Wrapper' satisfies FormFieldId] as string;
    const productVariation = { productType, productSubtype, wrapper };

    dispatch(workspaceQuoteReloaded('essential', tabId, productVariation));
    dispatch(
      commonFormActions.defaultStatifiedFormReceived(
        'essential',
        tabId,
        productView,
        buildProductDescriptionKey(values),
        getInstrumentName(values),
        blocksMetadata,
        fieldsMetadata,
        version,
        negotiationId,
        actions,
        quoteQuery,
        buildRemunerationMode(values),
        mustForceAdvancedMode,
        validationErrors,
        sendWholeFormData,
        productLayout,
      ),
    );

    dispatchCommonFormActions(dispatch, tabId, values, allowedRequests);

    dispatch(triggerDocumentationFetchThunk(tabId, 'essential'));

    dispatch(commonQuoteAction.previousQuoteLoadRequested('essential', tabId, quoteId));
  };
}

export function essentialReloadFormFromQuoteFailedThunk(identifier: string): Thunk<void> {
  return dispatch => {
    const toast = getToastUtils(dispatch);
    toast.warn(
      {
        id: 'essential.workspace.quote.import.error',
        values: {
          quoteId: identifier,
        },
      },
      { id: 'toast.essentialWorkspace' },
      false,
    );
  };
}
